import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  QRCode,
  Space,
  Typography,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { GoogleLogo, Key, LockOutlined, Password, SafetyOutlined, UnlockOutlined } from "@payping/icons";
import { useState } from "react";
import { accountApis } from "@payping/api";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { OtpCode } from "@/components/OtpCode";

interface FormType {
  googleAuthCode: string;
}

const LoginEnableDisable = () => {
  const [showEnableQrModal, setShowEnableQrModal] = useState<boolean>();
  const [showEnableCodeModal, setShowEnableCodeModal] = useState<boolean>();
  const [showDisableCodeModal, setShowDisableCodeModal] = useState<boolean>();
  const [isOtpValid, setIsOtpValid] = useState(false);

  const { t } = useAppTranslation();
  const [form] = Form.useForm<FormType>();

  const {
    loading: loadingIsTwoFAActive,
    value: isTwoFAActive,
    retry: retryIsTwoFAActive,
  } = useMessagedAsyncRetry(
    async () => {
      const state = await accountApis.getTwoFactorAuthenticationStatusPut();
      return state;
    },
    [],
    { useProgress: true },
  );

  const [{ loading: loadingQrCodeUrl, value: qrCodeUrl }, getQrCodeUrl] = useMessagedAsyncFn(
    async () => {
      const res = await accountApis.resetAuthenticatorKeyGet();
      return res.barcodeUrl;
    },
    [],
    undefined,
    { useProgress: true },
  );

  const [{ loading: loadingEnable }, handleEnable] = useMessagedAsyncFn(
    async (values: FormType) => {
      await accountApis.enableAuthenticatorWithRequestModelPost({ code: values.googleAuthCode });
      retryIsTwoFAActive();
      form.resetFields();
      setShowEnableCodeModal(false);
    },
    [retryIsTwoFAActive],
  );

  const [{ loading: loadingDisable }, handleDisable] = useMessagedAsyncFn(
    async (values: FormType) => {
      await accountApis.disableAuthenticatorWithCodePost(values.googleAuthCode);
      retryIsTwoFAActive();
      form.resetFields();
      setShowDisableCodeModal(false);
    },
    [retryIsTwoFAActive],
  );

  return (
    <>
      <Card.Ant>
        <div className={"tw-flex tw-justify-between"}>
          <Space>
            <SafetyOutlined width={16} />
            <Typography.Title level={5} className={"!tw-m-0"}>
              {t("user.2fa.loginTwoStepPassword")}
            </Typography.Title>
          </Space>
          {isTwoFAActive ? (
            <Button onClick={() => setShowDisableCodeModal(true)} loading={loadingIsTwoFAActive}>
              <Space>
                <UnlockOutlined width={14} className={"color--danger"} />
                <Typography.Text className={"color--danger"}>{t("common.deactivate")}</Typography.Text>
              </Space>
            </Button>
          ) : (
            <Button
              onClick={async () => {
                setShowEnableQrModal(true);
                await getQrCodeUrl();
              }}
              loading={loadingIsTwoFAActive}
            >
              <Space>
                <LockOutlined width={14} />
                {t("common.activate")}
              </Space>
            </Button>
          )}
        </div>
      </Card.Ant>

      <Modal
        title={t("user.2fa.modal.activate2fa")}
        open={showEnableQrModal}
        okText={t("user.2fa.modal.nextStep")}
        onOk={() => {
          setShowEnableQrModal(false);
          setShowEnableCodeModal(true);
        }}
        okButtonProps={{ loading: loadingQrCodeUrl }}
        cancelText={t("user.2fa.modal.close")}
        onCancel={() => setShowEnableQrModal(false)}
      >
        <Space direction={"vertical"}>
          <Space size={"large"}>
            <Password className={"color--gray500"} width={"64px"} />
            <GoogleLogo className={"color--gray500"} width={"64px"} />
          </Space>
          <Typography.Text type={"secondary"}>{t("user.2fa.modal.googleAuthDesc")}</Typography.Text>
          <QRCode status={loadingQrCodeUrl ? "loading" : undefined} iconSize={134} value={qrCodeUrl || ""} />
        </Space>
      </Modal>

      <Modal
        title={t("user.2fa.modal.activate2fa")}
        open={showEnableCodeModal}
        okText={t("common.activate")}
        onOk={() => form.submit()}
        okButtonProps={{ loading: loadingEnable, disabled: !isOtpValid }}
        cancelText={t("back")}
        onCancel={() => setShowEnableCodeModal(false)}
      >
        <Space size={"large"}>
          <Password className={"color--gray500"} width={"64px"} />
          <GoogleLogo className={"color--gray500"} width={"64px"} />
        </Space>
        <Space direction={"vertical"}>
          <Typography.Text type={"secondary"}>{t("user.2fa.modal.paypingCodeDesc")}</Typography.Text>
          <Form form={form} onFinish={handleEnable} layout={"vertical"}>
            <Form.Item label={t("user.2fa.modal.code")} name="googleAuthCode" rules={[{ required: true }]}>
              <Input.Password addonBefore={<Key width={"14px"} />} />
            </Form.Item>
            <OtpCode form={form} onVerify={(isVerified) => setIsOtpValid(isVerified)} />
          </Form>
        </Space>
      </Modal>

      <Modal
        title={t("user.2fa.disableLoginTwoStepPassword")}
        open={showDisableCodeModal}
        okText={t("common.deactivate")}
        onOk={() => form.submit()}
        okButtonProps={{ loading: loadingDisable, disabled: !isOtpValid }}
        cancelText={t("cancel")}
        onCancel={() => setShowDisableCodeModal(false)}
      >
        <Space size={"large"}>
          <Password className={"color--gray500"} width={"64px"} />
          <GoogleLogo className={"color--gray500"} width={"64px"} />
        </Space>
        <Space direction={"vertical"}>
          <Typography.Text type={"secondary"}>{t("user.2fa.modal.paypingCodeDesc")}</Typography.Text>
          <Form form={form} onFinish={handleDisable} layout={"vertical"}>
            <Form.Item label={t("user.2fa.modal.code")} name="googleAuthCode" rules={[{ required: true }]}>
              <Input.Password addonBefore={<Key width={"14px"} />} />
            </Form.Item>
            <OtpCode form={form} onVerify={(isVerified) => setIsOtpValid(isVerified)} />
          </Form>
        </Space>
      </Modal>
    </>
  );
};

export default LoginEnableDisable;
